import React, { useState, Component } from 'react';
import { HardHat, ChevronLeft, ChevronRight, ChevronDown, PanelLeftClose, PanelLeftOpen, LayoutDashboard, Bot, Users, ClipboardCheck, Wrench, FileWarning, FileCheck, ClipboardList, Eye, BarChart3, FileText, AlertTriangle, Building2, Shield, Sparkles } from 'lucide-react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
interface NavChild {
  name: string;
  path: string;
  tab?: string;
  icon: ComponentType<any>;
  aiTag?: boolean;
}
interface NavLink {
  name: string;
  path: string;
  icon: ComponentType<any>;
  children?: NavChild[];
  aiTag?: boolean;
}
const NAV_LINKS: NavLink[] = [{
  name: 'Dashboard',
  path: '/',
  icon: LayoutDashboard,
  children: [{
    name: 'Safety & Compliance',
    path: '/?tab=compliance',
    tab: 'compliance',
    icon: ClipboardList
  }, {
    name: 'Prevention',
    path: '/?tab=prevention',
    tab: 'prevention',
    icon: Shield,
    aiTag: true
  }]
}, {
  name: 'Hey Bilti !',
  path: '/bilton-ai',
  icon: Bot,
  aiTag: true
}, {
  name: 'Workers',
  path: '/workers',
  icon: Users
}, {
  name: 'Pre-Shift',
  path: '/pre-shift',
  icon: ClipboardCheck
}, {
  name: 'Toolbox Talk',
  path: '/toolbox-talk',
  icon: Wrench
}, {
  name: 'JHA',
  path: '/jha',
  icon: FileWarning
}, {
  name: 'PTP',
  path: '/ptp',
  icon: FileCheck
}, {
  name: 'Inspections & Logs',
  path: '/inspections',
  icon: ClipboardList
}, {
  name: 'Observations',
  path: '#',
  icon: Eye
}, {
  name: 'Reports',
  path: '#',
  icon: BarChart3
}, {
  name: 'Documents',
  path: '/documents',
  icon: FileText
}, {
  name: 'Incidents',
  path: '#',
  icon: AlertTriangle
}, {
  name: 'Contractors',
  path: '#',
  icon: Building2
}];
export function Navigation() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isPinned, setIsPinned] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const isExpanded = isPinned || isHovered;
  const currentTab = searchParams.get('tab');
  const AiTag = () => <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-500/30 animate-pulse" data-id="element-992">
      <Sparkles size={8} data-id="element-993" />
      AI
    </span>;
  return <motion.nav initial={false} animate={{
    width: isExpanded ? 240 : 64
  }} transition={{
    duration: 0.2,
    ease: 'easeInOut'
  }} className="h-screen bg-[#1a1a1a] text-white flex flex-col z-50 relative flex-shrink-0 border-r border-gray-800" onMouseEnter={() => {
    if (!isPinned) setIsHovered(true);
  }} onMouseLeave={() => {
    if (!isPinned) setIsHovered(false);
  }} data-id="element-994">
      {/* Top: Header with toggle + Logo */}
      <div className="h-[60px] flex items-center border-b border-gray-800 flex-shrink-0 px-2 gap-1" data-id="element-995">
        {/* Jira-style sidebar toggle button */}
        <button onClick={e => {
        e.stopPropagation();
        setIsPinned(!isPinned);
        if (isPinned) setIsHovered(false);
      }} className="w-8 h-8 flex items-center justify-center rounded-md text-gray-400 hover:text-white hover:bg-white/10 transition-all flex-shrink-0" title={isPinned ? 'Collapse sidebar' : 'Expand sidebar'} data-id="element-996">
          {isPinned ? <PanelLeftClose size={18} data-id="element-997" /> : <PanelLeftOpen size={18} data-id="element-998" />}
        </button>

        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 cursor-pointer hover:opacity-80 transition-opacity min-w-0" data-id="element-999">
          <div className="bg-yellow-500 p-1.5 rounded-full text-black flex-shrink-0" data-id="element-1000">
            <HardHat size={18} fill="currentColor" data-id="element-1001" />
          </div>
          {isExpanded && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="font-semibold text-sm whitespace-nowrap" data-id="element-1002">
              CHST Builders
            </motion.div>}
        </Link>
      </div>

      {/* Center: Navigation Links */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden no-scrollbar py-4 flex flex-col gap-1 px-2" data-id="element-1003">
        {NAV_LINKS.map(link => {
        const isDashboard = link.path === '/';
        const isActive = isDashboard ? location.pathname === '/' && !link.children : location.pathname === link.path || link.path !== '/' && location.pathname.startsWith(link.path);
        const isParentActive = isDashboard && location.pathname === '/';
        const Icon = link.icon;
        return <div key={link.name} data-id="element-1004">
              <Link to={link.children ? link.children[0]?.path || link.path : link.path} className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all relative group
                  ${isParentActive && !link.children ? 'bg-emerald-500/10 text-emerald-400' : ''}
                  ${isParentActive && link.children ? 'text-white' : ''}
                  ${!isParentActive && !isActive ? 'text-gray-400 hover:text-white hover:bg-white/5' : ''}
                  ${isActive && !isDashboard ? 'bg-emerald-500/10 text-emerald-400' : ''}
                `} title={!isExpanded ? link.name : undefined} data-id="element-1005">
                <Icon size={18} className="flex-shrink-0" data-id="element-1006" />

                {isExpanded && <span className={`text-sm whitespace-nowrap flex-1 flex items-center gap-2 ${isParentActive || isActive ? 'font-medium' : ''}`} data-id="element-1007">
                    {link.name}
                    {link.aiTag && <AiTag data-id="element-1008" />}
                  </span>}

                {isActive && !isExpanded && !link.children && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-emerald-400 rounded-r-full" data-id="element-1009" />}
              </Link>

              {/* Sub-items */}
              {link.children && isExpanded && <div className="ml-3 mt-1 flex flex-col gap-0.5 pl-3 border-l border-gray-700" data-id="element-1010">
                  {link.children.map(child => {
              const isChildActive = location.pathname === '/' && currentTab === child.tab;
              const ChildIcon = child.icon;
              return <Link key={child.name} to={child.path} className={`
                          flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all text-sm
                          ${isChildActive ? 'bg-emerald-500/15 text-emerald-400 font-semibold' : 'text-gray-400 hover:text-white hover:bg-white/5'}
                        `} data-id="element-1011">
                        <ChildIcon size={15} className={`flex-shrink-0 ${isChildActive ? 'text-emerald-400' : ''}`} data-id="element-1012" />
                        <span className="whitespace-nowrap flex items-center gap-2" data-id="element-1013">
                          {child.name}
                          {child.aiTag && <AiTag data-id="element-1014" />}
                        </span>
                      </Link>;
            })}
                </div>}
            </div>;
      })}
      </div>

      {/* Bottom: User Profile */}
      <div className="border-t border-gray-800 p-3 flex-shrink-0" data-id="element-1015">
        <div className="flex items-center gap-3 px-1" data-id="element-1016">
          <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0 text-sm font-bold text-gray-300" data-id="element-1017">
            JS
          </div>
          {isExpanded && <div className="min-w-0" data-id="element-1018">
              <p className="text-sm font-medium text-white truncate" data-id="element-1019">
                John Smith
              </p>
              <p className="text-[10px] text-gray-400 truncate" data-id="element-1020">
                Superintendent
              </p>
            </div>}
        </div>
      </div>
    </motion.nav>;
}